import React from "react";

import { Column } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { renderMilestoneDateByTypeAndReference } from "../../../../../../../../../../components/Generic/MilestoneDates";
import { MoreOptions } from "../../../../../../../../../../components/MoreOptionsComponent/MoreOptions";
import {
  MilestoneReference,
  MilestoneType,
  MilestoneValue,
} from "../../../../../../../../../../models/milestone";
import { capitalizeFirstLetter } from "../../../../../../../../../../util/capitalizeFirstLetter";
import { CronJobType } from "../../../../../../../../../../util/enums";
import { formatDateWithoutTimeZone } from "../../../../../../../../../../util/Format";
import ShipmentBadgeStatus from "../../../../../../../../SupplyChainPlus/Shipments/components/StatusBadge";
import { AirTypeLabel, SeaTypeLabel } from "../styles";

export const useShipmentColumns = () => {
  const { t: translate } = useTranslation();

  const renderShipmentType = (shipmentType: string) => {
    const isAirType = shipmentType?.toLocaleLowerCase() === "air";
    const label = capitalizeFirstLetter(shipmentType);

    if (isAirType) return <AirTypeLabel> {label}</AirTypeLabel>;

    return <SeaTypeLabel>{label} </SeaTypeLabel>;
  };

  const reportColumns: Column[] = [
    {
      name: "containerId",
      title: `${translate("container")} #`,
      searchIdentifier: "container_id",
      hideShowColumn: true,
    },
    {
      name: "shipmentType",
      title: translate("shipmentType"),
      formatComponent: (value: string) => value && renderShipmentType(value),
      hideSearch: true,
    },
    {
      name: "eventType",
      title: translate("project"),
      hideSearch: true,
    },
    { name: "hwab", title: translate("hawb"), hideSearch: true },
    { name: "id", title: `${translate("shipment")} #`, hideSearch: true },
    {
      name: "poNumbers",
      title: translate("orders"),
      formatComponent: (value) => (
        <MoreOptions
          options={value?.split(" / ").sort()}
          title={translate("orders")}
          manyOptionsToShow={1}
        ></MoreOptions>
      ),
      hideSearch: true,
    },
    {
      name: "milestones",
      title: translate("etaAtDestination"),
      formatText: (milestones: MilestoneValue[]) => {
        return renderMilestoneDateByTypeAndReference(
          milestones || [],
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.DESTINATION
        );
      },
      hideSearch: true,
      key: MilestoneReference.SHIPMENT.ESTIMATED.DESTINATION,
    },
    {
      name: "milestones",
      title: translate("estimateArrivalWH"),
      formatText: (milestones: MilestoneValue[]) => {
        return renderMilestoneDateByTypeAndReference(
          milestones || [],
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.WAREHOUSE
        );
      },
      hideSearch: true,
      key: MilestoneReference.SHIPMENT.ESTIMATED.WAREHOUSE,
    },
    {
      name: "milestones",
      title: translate("unpackDate"),
      formatText: (milestones: MilestoneValue[]) => {
        return renderMilestoneDateByTypeAndReference(
          milestones || [],
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.UNPACK
        );
      },
      hideSearch: true,
      key: MilestoneReference.SHIPMENT.ESTIMATED.UNPACK,
    },
    {
      name: "arrivalFinalPort",
      title: translate("finalPortArrival"),
      formatText: (value?: string) => (value ? formatDateWithoutTimeZone(value) : ""),
      hideSearch: true,
    },
    { name: "dcDestination", title: translate("destination") },
    {
      name: "statusShipment",
      title: translate("status"),
      formatComponent: (status: string) => status && <ShipmentBadgeStatus value={status} />,
      hideSearch: true,
    },
  ];

  const isReceived = (row: any) => {
    if (row?.cartonId && !row.isException) {
      return 1;
    }
    if (!row?.cartonId && row?.isException) {
      return 1;
    }

    return 0;
  };

  const getCrossDockStatus = (row: any) => {
    if (row?.cartonId && !row?.isException) {
      return translate("receivedExpected");
    }

    if (row?.cartonId && row?.isException) {
      return translate("notReceivedExpected");
    }

    return translate("receivedUnexpected");
  };

  const crossDockColumns: Column[] = [
    {
      name: "sku",
      title: translate("sku"),
      searchIdentifier: "sku",
    },
    {
      name: "cartonId",
      title: translate("expectedQty"),
      formatText: (value: number) => (value ? "1" : "0"),
      searchIdentifier: "expected",
    },
    {
      name: "cartonId",
      title: translate("quantityReceived"),
      formatComponent: (value: number, row: any) => isReceived(row),
      searchIdentifier: "received",
    },
    {
      name: "containerConference",
      title: translate("dateReceived"),
      searchIdentifier: "date_conference",
      formatText: (value?: any) =>
        value?.dateConference ? formatDateWithoutTimeZone(value?.dateConference) : "",
    },
    {
      name: "cartonId",
      title: translate("status"),
      formatComponent: (value: number, row: any) => getCrossDockStatus(row),
      searchIdentifier: "status_grid",
    },
  ];

  const getShipmentReportColumns = (type: CronJobType) => {
    switch (type) {
      case CronJobType.REPORT:
        return reportColumns;
      case CronJobType.CROSS_DOCK_STATUS:
        return crossDockColumns;
      default:
        return reportColumns;
    }
  };

  const getShipmentReportRequiredFilters = (type: CronJobType) => {
    switch (type) {
      case CronJobType.REPORT:
        return [
          {
            name: "event_type",
            message: translate("selectAProject"),
            title: translate("projectName"),
          },
          {
            name: "period",
            message: translate("selectAPeriod"),
            title: translate("period"),
          },
        ];
      case CronJobType.CROSS_DOCK_STATUS:
        return [
          {
            name: "date",
            message: translate("selectAStartDate"),
            title: translate("date"),
          },
          {
            name: "project",
            message: translate("selectAProject"),
            title: translate("projectName"),
          },
          {
            name: "container",
            message: translate("selectAContainer"),
            title: `${translate("container")} #`,
          },
        ];
      default:
        return [];
    }
  };

  return { getShipmentReportColumns, getShipmentReportRequiredFilters };
};
